/**
 * Cross-department date-conflict detection for assignees.
 *
 * Business rule: a member can belong to multiple departments (Civil + Land,
 * for example), but their schedule cannot have two tasks whose date ranges
 * overlap when those tasks live in *different* departments. Same-dept
 * overlaps are fine — that is just how dependent stages stack inside one team.
 */

import { differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';
import type { EpcScheduleTaskRow } from '../../types/scheduleTask';
import { taskAssignedUserId, taskOrgUnitId } from './epcScheduleDepartmentAssignment';

function parseDay(raw: unknown): Date | null {
  if (raw == null || String(raw).trim() === '') return null;
  const d = parseISO(String(raw).slice(0, 10));
  return isValid(d) ? d : null;
}

function taskDateRange(t: EpcScheduleTaskRow): { start: Date; end: Date } | null {
  const s = parseDay(t.start);
  const f = parseDay(t.finish);
  const start = s ?? f;
  const end = f ?? s;
  if (!start || !end) return null;
  return start.getTime() <= end.getTime()
    ? { start, end }
    : { start: end, end: start };
}

function rangesOverlap(
  a: { start: Date; end: Date },
  b: { start: Date; end: Date },
): boolean {
  return a.start.getTime() <= b.end.getTime() && b.start.getTime() <= a.end.getTime();
}

function enumerateOverlapDays(
  a: { start: Date; end: Date },
  b: { start: Date; end: Date },
): string[] {
  const start = a.start.getTime() > b.start.getTime() ? a.start : b.start;
  const end = a.end.getTime() < b.end.getTime() ? a.end : b.end;
  const span = differenceInCalendarDays(end, start);
  if (span < 0) return [];
  const out: string[] = [];
  const cap = Math.min(span, 30);
  for (let i = 0; i <= cap; i += 1) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    out.push(format(d, 'yyyy-MM-dd'));
  }
  return out;
}

export type DateConflict = {
  userId: string;
  taskAIndex: number;
  taskBIndex: number;
  taskAName: string;
  taskBName: string;
  orgIdA: string;
  orgIdB: string;
  /** Up to 31 overlap days as `YYYY-MM-DD`. Truncated for very long stages. */
  overlapDates: string[];
  overlapStart: Date;
  overlapEnd: Date;
};

/**
 * All cross-department date conflicts for a single user.
 *
 * Two tasks A and B form a conflict when:
 *   • both are assigned to `userId`
 *   • both have a department (orgUnitId)
 *   • A.orgUnitId !== B.orgUnitId
 *   • their inclusive date ranges overlap on at least one calendar day
 */
export function findUserCrossDeptConflicts(
  tasks: EpcScheduleTaskRow[],
  userId: string,
): DateConflict[] {
  type Owned = {
    idx: number;
    task: EpcScheduleTaskRow;
    orgId: string;
    range: { start: Date; end: Date };
  };
  const owned: Owned[] = [];
  for (let i = 0; i < tasks.length; i += 1) {
    const t = tasks[i]!;
    if (taskAssignedUserId(t) !== userId) continue;
    const orgId = taskOrgUnitId(t);
    if (!orgId) continue;
    const range = taskDateRange(t);
    if (!range) continue;
    owned.push({ idx: i, task: t, orgId, range });
  }
  const out: DateConflict[] = [];
  for (let a = 0; a < owned.length; a += 1) {
    for (let b = a + 1; b < owned.length; b += 1) {
      const A = owned[a]!;
      const B = owned[b]!;
      if (A.orgId === B.orgId) continue;
      if (!rangesOverlap(A.range, B.range)) continue;
      const overlapStart =
        A.range.start.getTime() > B.range.start.getTime() ? A.range.start : B.range.start;
      const overlapEnd =
        A.range.end.getTime() < B.range.end.getTime() ? A.range.end : B.range.end;
      out.push({
        userId,
        taskAIndex: A.idx,
        taskBIndex: B.idx,
        taskAName: String(A.task.name ?? '').trim() || `Row ${A.idx + 1}`,
        taskBName: String(B.task.name ?? '').trim() || `Row ${B.idx + 1}`,
        orgIdA: A.orgId,
        orgIdB: B.orgId,
        overlapDates: enumerateOverlapDays(A.range, B.range),
        overlapStart,
        overlapEnd,
      });
    }
  }
  return out;
}

/** Map of `userId → conflicts` for every assignee with at least one conflict. */
export function findAllCrossDeptConflicts(
  tasks: EpcScheduleTaskRow[],
): Map<string, DateConflict[]> {
  const seen = new Set<string>();
  for (const t of tasks) {
    const u = taskAssignedUserId(t);
    if (u) seen.add(u);
  }
  const out = new Map<string, DateConflict[]>();
  for (const uid of seen) {
    const list = findUserCrossDeptConflicts(tasks, uid);
    if (list.length > 0) out.set(uid, list);
  }
  return out;
}

export type AssignmentConflict = {
  /** Original task index that would create the conflict. */
  candidateIndex: number;
  candidateName: string;
  candidateOrgId: string;
  /** Task already on the user (in a different dept) that overlaps. */
  existingIndex: number;
  existingName: string;
  existingOrgId: string;
  overlapStart: Date;
  overlapEnd: Date;
};

/**
 * Predict whether assigning `userId` to the tasks at `candidateIndices` would
 * create cross-department date overlap with that user's *other* tasks.
 *
 * Each candidate is checked against all other tasks already assigned to the
 * user (excluding candidates themselves, so re-assigning a stack inside one
 * department does not flag itself). Same-department overlaps are ignored.
 */
export function predictAssignmentConflicts(
  tasks: EpcScheduleTaskRow[],
  candidateIndices: Iterable<number>,
  userId: string,
): AssignmentConflict[] {
  const candidateSet = new Set<number>();
  for (const i of candidateIndices) candidateSet.add(i);
  if (candidateSet.size === 0) return [];

  type Existing = {
    idx: number;
    name: string;
    orgId: string;
    range: { start: Date; end: Date };
  };
  const existing: Existing[] = [];
  for (let i = 0; i < tasks.length; i += 1) {
    if (candidateSet.has(i)) continue;
    const t = tasks[i]!;
    if (taskAssignedUserId(t) !== userId) continue;
    const orgId = taskOrgUnitId(t);
    if (!orgId) continue;
    const range = taskDateRange(t);
    if (!range) continue;
    existing.push({
      idx: i,
      name: String(t.name ?? '').trim() || `Row ${i + 1}`,
      orgId,
      range,
    });
  }
  if (existing.length === 0) return [];

  const out: AssignmentConflict[] = [];
  for (const ci of candidateSet) {
    const ct = tasks[ci];
    if (!ct) continue;
    const candOrgId = taskOrgUnitId(ct);
    if (!candOrgId) continue;
    const candRange = taskDateRange(ct);
    if (!candRange) continue;
    const candName = String(ct.name ?? '').trim() || `Row ${ci + 1}`;
    for (const e of existing) {
      if (e.orgId === candOrgId) continue;
      if (!rangesOverlap(candRange, e.range)) continue;
      const overlapStart =
        candRange.start.getTime() > e.range.start.getTime()
          ? candRange.start
          : e.range.start;
      const overlapEnd =
        candRange.end.getTime() < e.range.end.getTime() ? candRange.end : e.range.end;
      out.push({
        candidateIndex: ci,
        candidateName: candName,
        candidateOrgId: candOrgId,
        existingIndex: e.idx,
        existingName: e.name,
        existingOrgId: e.orgId,
        overlapStart,
        overlapEnd,
      });
    }
  }
  return out;
}

export function formatOverlapRange(c: { overlapStart: Date; overlapEnd: Date }): string {
  const a = format(c.overlapStart, 'dd MMM yyyy');
  const b = format(c.overlapEnd, 'dd MMM yyyy');
  return a === b ? a : `${a} → ${b}`;
}
